/* eslint-disable max-lines */
import { memo, useEffect, useRef, useState } from "react"
import type {
  ComponentProps,
  Dispatch,
  FormEvent,
  KeyboardEvent,
  MouseEvent,
  PointerEvent,
  ReactNode,
  Ref,
  SetStateAction,
} from "react"

import { useQueryClient } from "@tanstack/react-query"
import { format } from "date-fns"
import { sv } from "date-fns/locale"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import type { ChatStore } from "../hooks/chat-request-registry"
import type { ChatSessionModel } from "@/api/models"
import type { SessionType } from "@/types"

import { acceptTitleSuggestion, dismissTitleSuggestion } from "@/api/api"
import {
  BookOpen,
  Check,
  CheckCircle2,
  Circle,
  Cloud,
  Crown,
  EllipsisVertical,
  EyeOff,
  Folder,
  FolderInput,
  Globe,
  Lightbulb,
  Loader2,
  MessageSquareText,
  Mic,
  Pencil,
  Share2,
  Star,
  Telescope,
  Trash2,
  UserMinus,
  Users,
  Workflow,
  X,
} from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useRoutes } from "@/contexts/use-routes"
import { getChatSharingState } from "@/features/chat/utils/chat-sharing-state"
import { MoveToProjectDialog } from "@/features/projects/components/move-to-project-dialog"
import {
  ParticipantAvatar,
  ParticipantFacepile,
} from "@/features/sharing/components/participant-avatar"
import { usePrefetchSession } from "@/hooks/use-prefetch"
import { useLeaveSharedSession } from "@/hooks/use-session-sharing"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"

import { AnimatedTitle } from "./animated-title"
import { ChatHistoryItemTitleIndicator } from "./chat-history-item-title-indicator"
import { shouldShowChatUnreadIndicator } from "./chat-history-item-utils"
import { ChatShareDialog } from "./chat-share-dialog"
import {
  getSharedSessionAccessState,
  getSharedSessionActionLabel,
  getSharedSessionConfirmDescription,
  getSharedSessionConfirmTitle,
  showSharedSessionActionErrorToast,
  showSharedSessionActionSuccessToast,
} from "./shared-session-leave-utils"
import { useUpdateSessionProject } from "../hooks/use-update-session-project"

type ChatHistoryItemProps = {
  allVisibleSessions: ChatSessionModel[]
  // The store this chat list belongs to; prefetch and sharing read the chat's
  // current query identity from it (#3524).
  chatStore: ChatStore
  currentSessionId: string | null
  hideSharing?: boolean
  isFavorited: boolean
  isProcessing?: boolean
  isUnread?: boolean
  lastSelectedIndex: number | null
  navigateOnSelect?: boolean
  renameIsPending: boolean
  renamingSessionId: string | null
  selectedSessions: ChatSessionModel[]
  selectMode: boolean
  session: ChatSessionModel
  sessionIndex: number
  sessionType: SessionType
  setLastSelectedIndex: Dispatch<SetStateAction<number | null>>
  setSelectedSessions: Dispatch<SetStateAction<ChatSessionModel[]>>
  setShowDeleteSessionAlertDialog: Dispatch<SetStateAction<boolean>>
  toggleFavoriteSession: (session: ChatSessionModel) => void
  onCancelRenaming: () => void
  onDropdownMenuOpen: (isOpen: boolean, session: ChatSessionModel) => void
  onRenameSession: (session: ChatSessionModel, newTitle: string) => Promise<void> | void
  onSelectModeToggle?: (session: ChatSessionModel) => void
  onSelectSession: (session: ChatSessionModel) => void
  onSharedLeaveRedirect?: () => void
  onStartRenaming: (session: ChatSessionModel) => void
}

// While renaming, the row hosts a form (inputs and buttons), so the primary
// target stays a plain div; otherwise it is the real open-chat <button>.
// Rendered through Radix `asChild`, so it must forward its props and ref.
function OpenChatTarget({
  children,
  ref,
  ...props
}: ComponentProps<"button"> & { ref?: Ref<HTMLElement> }) {
  const isButton = props.type === "button"
  return isButton ? (
    <button ref={ref as Ref<HTMLButtonElement>} {...props}>
      {children}
    </button>
  ) : (
    <div ref={ref as Ref<HTMLDivElement>} {...(props as ComponentProps<"div">)}>
      {children}
    </div>
  )
}

function SidebarChatMenuItemContent({ icon, label }: { icon: ReactNode; label: ReactNode }) {
  return (
    <span className="flex items-center gap-2">
      <span className="flex size-4 shrink-0 items-center justify-center">{icon}</span>
      <span>{label}</span>
    </span>
  )
}

function ChatHistoryItemInner({
  chatStore,
  session,
  onSelectSession,
  currentSessionId,
  selectedSessions,
  setSelectedSessions,
  onDropdownMenuOpen,
  setShowDeleteSessionAlertDialog,
  toggleFavoriteSession,
  hideSharing = false,
  isFavorited,
  isProcessing = false,
  isUnread = false,
  selectMode,
  lastSelectedIndex,
  setLastSelectedIndex,
  sessionIndex,
  sessionType,
  allVisibleSessions,
  onStartRenaming,
  onRenameSession,
  onCancelRenaming,
  renamingSessionId,
  renameIsPending,
  navigateOnSelect = false,
  onSelectModeToggle,
  onSharedLeaveRedirect,
}: ChatHistoryItemProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const settingsService = SettingsService.getInstance()
  const navigate = useNavigate()
  const { getRouteChatPath } = useRoutes()

  if (!session) return null

  const isSelected = session.id === currentSessionId
  const showUnreadIndicator = shouldShowChatUnreadIndicator({
    hasResponseUnread: isUnread,
    hasSharingNotification: Boolean(session.hasUnviewedNotification),
    isProcessing,
  })
  const renameConfirmLabel = t("chatsession.rename_confirm", { defaultValue: "Save title" })
  const renameCancelLabel = t("chatsession.rename_cancel", { defaultValue: "Cancel renaming" })
  const isRenaming = Boolean(session.id && renamingSessionId === session.id)
  const [renameValue, setRenameValue] = useState(session.title ?? "")
  const inputRef = useRef<HTMLInputElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [showMoveDialog, setShowMoveDialog] = useState(false)
  const [showShareDialog, setShowShareDialog] = useState(false)
  const [showLeaveSharedDialog, setShowLeaveSharedDialog] = useState(false)
  const [isUpdatingTitleSuggestion, setIsUpdatingTitleSuggestion] = useState(false)
  const longPressTimerRef = useRef<number | null>(null)
  const longPressTriggeredRef = useRef(false)
  const startPositionRef = useRef<{ x: number; y: number } | null>(null)
  const longPressThresholdMs = 500
  const maxMovementPx = 10
  const leaveSharedSession = useLeaveSharedSession()

  const currentUserOid = settingsService.claims.oid
  const currentUserGroups = (settingsService.claims.groups ?? []).filter((group): group is string =>
    Boolean(group)
  )
  const currentUserGroupSet = new Set(currentUserGroups)
  const participants = session.participants ?? []
  const { hasDirectShare, hasGroupShare } = getSharedSessionAccessState(
    participants.map((participant) => ({
      oid: participant.oid,
      type: participant.type,
    })),
    currentUserOid,
    currentUserGroupSet
  )
  const sharingState = getChatSharingState(session, currentUserOid)
  const canManageOwnedSession =
    session.currentUserRole === "owner" ||
    (!session.currentUserRole && session.userId === currentUserOid)
  const canLeaveOrHideSharedSession =
    session.currentUserRole === "reader" &&
    !hideSharing &&
    Boolean(session.id) &&
    (hasDirectShare || hasGroupShare)
  const shouldRenderSessionMenu = canManageOwnedSession || canLeaveOrHideSharedSession
  const sharedActionLabel = getSharedSessionActionLabel(t, hasDirectShare)

  const { moveToProject } = useUpdateSessionProject({
    sessionType: session.sessionType ?? "open",
    onSuccess: () => {
      setShowMoveDialog(false)
    },
  })

  const prefetchSession = usePrefetchSession(chatStore)

  // Cleanup prefetch timeout on unmount
  useEffect(() => {
    return () => {
      if (prefetchTimeoutRef.current) {
        clearTimeout(prefetchTimeoutRef.current)
      }
    }
  }, [])

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }

  const handleItemPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "touch") return
    if (selectMode || isRenaming) return
    longPressTriggeredRef.current = false
    startPositionRef.current = { x: event.clientX, y: event.clientY }
    clearLongPressTimer()
    longPressTimerRef.current = window.setTimeout(() => {
      longPressTriggeredRef.current = true
      setIsMenuOpen(true)
      onDropdownMenuOpen(true, session)
    }, longPressThresholdMs)
  }

  const handleItemPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "touch") return
    if (!startPositionRef.current) return
    const dx = Math.abs(event.clientX - startPositionRef.current.x)
    const dy = Math.abs(event.clientY - startPositionRef.current.y)
    if (Math.max(dx, dy) > maxMovementPx) {
      clearLongPressTimer()
    }
  }

  const handleItemPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "touch") return
    clearLongPressTimer()
    if (longPressTriggeredRef.current) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  const handleItemPointerCancel = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "touch") return
    clearLongPressTimer()
  }

  const handleItemContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    if (longPressTriggeredRef.current) {
      event.preventDefault()
    }
  }

  useEffect(() => {
    return () => {
      clearLongPressTimer()
    }
  }, [])

  useEffect(() => {
    setRenameValue(session.title ?? "")

    if (!isRenaming) {
      return
    }

    const timeoutId = setTimeout(() => {
      const input = inputRef.current
      if (input) {
        input.focus()
        input.select()
      }
    }, 0)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [isRenaming, session.title])

  const trimmedRenameValue = renameValue.trim()
  const isConfirmDisabled =
    renameIsPending ||
    trimmedRenameValue.length === 0 ||
    trimmedRenameValue === (session.title ?? "").trim()

  const handleRenameSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    event.stopPropagation()
    await onRenameSession(session, renameValue)
  }

  const handleRenameCancel = () => {
    setRenameValue(session.title ?? "")
    onCancelRenaming()
  }

  const handleMoveToProject = (projectId: string) => {
    if (!session.id) return
    void moveToProject(session.id, projectId)
  }

  const handleConfirmLeaveOrHideSharedSession = async () => {
    if (!session.id) return

    try {
      const result = await leaveSharedSession.mutateAsync({ sessionId: session.id })
      showSharedSessionActionSuccessToast(t, result.action)

      setShowLeaveSharedDialog(false)
      if (isSelected) {
        onSharedLeaveRedirect?.()
      }
    } catch {
      showSharedSessionActionErrorToast(t)
    }
  }

  const handleRenameInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      handleRenameCancel()
    }
  }

  function resolveIconType():
    | "agent"
    | "flow"
    | "prompt_template"
    | "web_search"
    | "m365"
    | "voice"
    | "chat" {
    if (!session) return "chat"
    // Prefer iconType for voice (it's set at session creation), then lastFunctionUsed, fallback to chat
    // Voice sessions should retain their microphone icon even after other functions are used
    if (session.iconType === "voice") {
      return "voice"
    }
    // Prefer lastFunctionUsed (server truth) over iconType; fallback to chat
    const rawType = (session.lastFunctionUsed || session.iconType || "chat") as string
    const normalized = rawType.toString().trim().toLowerCase().replaceAll("-", "_") as
      | "agent"
      | "flow"
      | "prompt_template"
      | "web_search"
      | "m365"
      | "voice"
      | "chat"
      | string

    const mapped: Record<
      string,
      "agent" | "flow" | "prompt_template" | "web_search" | "m365" | "voice" | "chat"
    > = {
      agent: "agent",
      flow: "flow",
      prompt_template: "prompt_template",
      prompttemplate: "prompt_template",
      web_search: "web_search",
      websearch: "web_search",
      microsoft_365_search: "m365",
      m365: "m365",
      voice: "voice",
      chat: "chat",
    }

    return mapped[normalized] ?? "chat"
  }

  function getIconGlyph() {
    if (!session) return null
    if (session.projectId) {
      return <Folder className="size-4 shrink-0" />
    }

    const type = resolveIconType()

    switch (type) {
      case "agent":
        return <Telescope className="size-4 shrink-0" />
      case "flow":
        return <Workflow className="size-4 shrink-0" />
      case "prompt_template":
        return <BookOpen className="size-4 shrink-0" />
      case "web_search":
        return <Globe className="size-4 shrink-0" />
      case "m365":
        return <Cloud className="size-4 shrink-0" />
      case "voice":
        return <Mic className="size-4 shrink-0" />
      case "chat":
      default:
        return <MessageSquareText className="size-4 shrink-0" />
    }
  }

  // Debounce prefetch to avoid loading messages for many sessions when scrolling/hovering
  const prefetchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleMouseEnter = () => {
    if (session.id && !isSelected) {
      // Clear any pending prefetch
      if (prefetchTimeoutRef.current) {
        clearTimeout(prefetchTimeoutRef.current)
      }
      // Debounce prefetch by 300ms - only prefetch if user hovers for a bit
      const sessionId = session.id
      prefetchTimeoutRef.current = setTimeout(() => {
        if (sessionId) {
          prefetchSession(sessionId)
        }
        prefetchTimeoutRef.current = null
      }, 300)
    }
  }

  const handleMouseLeave = () => {
    // Cancel prefetch if user moves mouse away before debounce completes
    if (prefetchTimeoutRef.current) {
      clearTimeout(prefetchTimeoutRef.current)
      prefetchTimeoutRef.current = null
    }
  }

  // Activation logic for a row click — select-mode range/toggle, rename-guard,
  // and navigate-vs-select. Keyboard users activate the inner open-chat
  // <button>, whose native click bubbles up to the row's handleClick (the row
  // itself is presentation-only).
  const activateSession = (shiftKey: boolean) => {
    if (selectMode) {
      if (shiftKey && lastSelectedIndex !== null) {
        const startIndex = Math.min(lastSelectedIndex, sessionIndex)
        const endIndex = Math.max(lastSelectedIndex, sessionIndex)
        const rangeSessions = allVisibleSessions.slice(startIndex, endIndex + 1)
        setSelectedSessions(rangeSessions)
        setLastSelectedIndex(sessionIndex)
      } else {
        const isChecked = selectedSessions.some((s) => s != null && s.id === session.id)
        if (!isChecked) {
          setSelectedSessions([...selectedSessions, session])
          setLastSelectedIndex(sessionIndex)
        } else {
          setSelectedSessions(selectedSessions.filter((s) => s != null && s.id !== session.id))
        }
      }
      return
    }

    if (isRenaming) {
      return
    }

    if (navigateOnSelect) {
      void navigate(`${getRouteChatPath(session.sessionType ?? "open")}/${session.id}`)
    } else {
      onSelectSession(session)
    }
  }

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (longPressTriggeredRef.current) {
      e.preventDefault()
      e.stopPropagation()
      longPressTriggeredRef.current = false
      return
    }
    if (selectMode) {
      e.preventDefault()
    } else if (isRenaming) {
      e.preventDefault()
      e.stopPropagation()
    }
    activateSession(e.shiftKey)
  }

  const handleSelectModeMenuClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
    onSelectModeToggle?.(session)
  }

  const handleAcceptTitleSuggestion = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (!session.id || !session.pending_title_suggestion?.suggested_title) return

    setIsUpdatingTitleSuggestion(true)
    try {
      await acceptTitleSuggestion(
        session.id,
        session.pending_title_suggestion.suggested_title,
        settingsService.token
      )
      await queryClient.invalidateQueries({
        queryKey: ["session-history"],
      })
    } catch {
      toast.error(t("dynamic_titles.toast_error", { defaultValue: "Failed to rename chat" }))
    } finally {
      setIsUpdatingTitleSuggestion(false)
    }
  }

  const handleDismissTitleSuggestion = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (!session.id) return

    setIsUpdatingTitleSuggestion(true)
    try {
      await dismissTitleSuggestion(session.id, settingsService.token)
      await queryClient.invalidateQueries({
        queryKey: ["session-history"],
      })
    } catch {
      toast.error(t("dynamic_titles.toast_error", { defaultValue: "Failed to rename chat" }))
    } finally {
      setIsUpdatingTitleSuggestion(false)
    }
  }

  // Leading glyph (left of the title): the chat-type icon by default, or a crown
  // for ownership transfer. Always visible (identity), unlike the trailing marker
  // (sharer avatar for incoming, recipient facepile for outgoing) which yields to
  // the hover menu. Suppressed entirely when sharing is hidden (e.g. AI Parser).
  const renderLeadingGlyph = () => {
    if (hideSharing) return null
    const wrap = "flex size-[18px] shrink-0 items-center justify-center"
    if (sharingState.isOwnerTransfer) {
      const ownerTooltipLabel = t("chatsession.made_owner_tooltip", {
        defaultValue: "You were made owner",
      })
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              aria-label={ownerTooltipLabel}
              className={cn(wrap, "text-warning rounded-sm")}
              role="img"
            >
              <Crown className="size-4" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={4}>
            <p>{ownerTooltipLabel}</p>
          </TooltipContent>
        </Tooltip>
      )
    }
    return <span className={cn(wrap, "text-muted-foreground")}>{getIconGlyph()}</span>
  }

  // Trailing marker (right of the title): recipient facepile for outgoing
  // shares, or an "Owner" tag for ownership transfer. Fades out on hover so the
  // dropdown-menu trigger can take its place.
  const renderTrailingMarker = () => {
    if (hideSharing) return null
    const markerClass = cn(
      "shrink-0 transition-opacity duration-150 group-hover/item:opacity-0",
      isMenuOpen && "opacity-0"
    )
    if (sharingState.isOwnerTransfer) {
      return (
        <span
          className={cn(
            markerClass,
            "bg-warning/15 text-warning-foreground rounded px-1.5 py-0.5 text-[10px] font-medium"
          )}
        >
          {t("sharing.role_owner", { defaultValue: "Owner" })}
        </span>
      )
    }
    if (sharingState.relationship === "shared_with_me") {
      return sharingState.sharer ? (
        <ParticipantAvatar className={markerClass} participant={sharingState.sharer} size={18} />
      ) : (
        <span className={markerClass}>
          <Users className="text-muted-foreground size-3.5" />
        </span>
      )
    }
    if (sharingState.relationship === "shared_by_me" && sharingState.recipients.length > 0) {
      return (
        <ParticipantFacepile
          className={markerClass}
          max={3}
          participants={sharingState.recipients}
          size={18}
        />
      )
    }
    return null
  }

  return (
    <>
      <Tooltip>
        <div
          data-testid="chat-history-item"
          role="presentation"
          className={cn(
            "group/item relative isolate flex w-full cursor-pointer items-center rounded-md",
            "hover:text-sidebar-accent-foreground",
            isSelected && "bg-sidebar-accent text-sidebar-accent-foreground",
            isRenaming && !isSelected && "border-sidebar-accent/40 bg-sidebar-accent/20 border"
          )}
          onClick={handleClick}
          onContextMenu={handleItemContextMenu}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onPointerCancel={handleItemPointerCancel}
          onPointerDown={handleItemPointerDown}
          onPointerMove={handleItemPointerMove}
          onPointerUp={handleItemPointerUp}
        >
          {!isSelected && (
            <div
              aria-hidden="true"
              className="bg-sidebar-accent pointer-events-none absolute inset-0 -z-10 rounded-md opacity-0 transition-opacity duration-150 group-hover/item:opacity-100"
            />
          )}
          <TooltipTrigger asChild>
            <OpenChatTarget
              className={cn(
                "flex w-full items-center justify-start gap-2 px-2 py-1.5",
                !isRenaming &&
                  "focus-visible:ring-ring rounded-md text-left focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
              )}
              {...(isRenaming
                ? {}
                : { type: "button" as const, "aria-label": session.title || undefined })}
            >
              {isRenaming ? (
                <form className="flex w-full items-center gap-2" onSubmit={handleRenameSubmit}>
                  <Input
                    ref={inputRef}
                    aria-label={t("chatsession.rename", { defaultValue: "Rename session" })}
                    className="h-7 w-full text-sm"
                    disabled={renameIsPending}
                    value={renameValue}
                    onChange={(event) => setRenameValue(event.target.value)}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={handleRenameInputKeyDown}
                    onPointerDown={(event) => event.stopPropagation()}
                  />
                  <div className="flex items-center gap-1">
                    <Button
                      aria-label={renameConfirmLabel}
                      className="size-6"
                      disabled={isConfirmDisabled}
                      size="icon"
                      type="submit"
                      variant="ghost"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {renameIsPending ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : (
                        <Check className="size-3.5" />
                      )}
                    </Button>
                    <Button
                      aria-label={renameCancelLabel}
                      className="size-6"
                      size="icon"
                      type="button"
                      variant="ghost"
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        handleRenameCancel()
                      }}
                    >
                      <X className="size-3.5" />
                    </Button>
                  </div>
                </form>
              ) : (
                <>
                  {renderLeadingGlyph()}
                  <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                    <span className="flex min-w-0 flex-1 items-center gap-1.5">
                      <AnimatedTitle
                        animationSpeed={25}
                        className="truncate text-left text-sm"
                        title={session.title ?? ""}
                      />
                      <ChatHistoryItemTitleIndicator
                        pending_title_suggestion={session.pending_title_suggestion}
                      />
                      {isProcessing && (
                        <Loader2 className="text-primary size-3 shrink-0 animate-spin" />
                      )}
                      {showUnreadIndicator && (
                        <span
                          className="bg-primary size-2 shrink-0 rounded-full"
                          data-testid="chat-history-item-unread-indicator"
                        />
                      )}
                    </span>
                    {renderTrailingMarker()}
                  </span>
                </>
              )}
            </OpenChatTarget>
          </TooltipTrigger>
          {!isRenaming && !selectMode && shouldRenderSessionMenu && (
            <DropdownMenu
              open={isMenuOpen}
              onOpenChange={(isOpen) => {
                setIsMenuOpen(isOpen)
                onDropdownMenuOpen(isOpen, session)
              }}
            >
              <DropdownMenuTrigger
                aria-disabled={selectMode}
                data-testid={`chat-history-item-dropdown-menu-trigger-${session.id}`}
                className={cn(
                  "absolute top-1/2 right-1 -translate-y-1/2 rounded-md p-1 transition-opacity duration-150",
                  "opacity-0 group-hover/item:opacity-100",
                  "hover:bg-sidebar-accent-foreground/15",
                  isMenuOpen && "opacity-100"
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <EllipsisVertical className="size-3.5 shrink-0" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="min-w-[180px]" side="right">
                {canManageOwnedSession && (
                  <>
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={(e) => {
                        e.stopPropagation()
                        onStartRenaming(session)
                      }}
                    >
                      <SidebarChatMenuItemContent
                        icon={<Pencil className="size-4" />}
                        label={t("chatsession.rename", { defaultValue: "Rename" })}
                      />
                    </DropdownMenuItem>
                    {!hideSharing && (
                      <DropdownMenuItem
                        className="gap-2"
                        onClick={(e) => {
                          e.stopPropagation()
                          setShowShareDialog(true)
                        }}
                      >
                        <SidebarChatMenuItemContent
                          icon={<Share2 className="size-4" />}
                          label={t("chatsession.share", { defaultValue: "Share" })}
                        />
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowDeleteSessionAlertDialog(true)
                      }}
                    >
                      <SidebarChatMenuItemContent
                        icon={<Trash2 className="size-4" />}
                        label={t("chatsession.delete")}
                      />
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleFavoriteSession(session)
                      }}
                    >
                      <SidebarChatMenuItemContent
                        icon={<Star className="size-4" />}
                        label={
                          isFavorited ? t("chatsession.unfavorite") : t("chatsession.favorite")
                        }
                      />
                    </DropdownMenuItem>
                    {!session.projectId && sessionType !== "ai-parser" && (
                      <DropdownMenuItem
                        className="gap-2"
                        onClick={(e) => {
                          e.stopPropagation()
                          setShowMoveDialog(true)
                        }}
                      >
                        <SidebarChatMenuItemContent
                          icon={<FolderInput className="size-4" />}
                          label={t("chatsession.move_to_project", {
                            defaultValue: "Move to project",
                          })}
                        />
                      </DropdownMenuItem>
                    )}
                    {onSelectModeToggle && (
                      <DropdownMenuItem className="gap-2" onClick={handleSelectModeMenuClick}>
                        <SidebarChatMenuItemContent
                          icon={<CheckCircle2 className="size-4" />}
                          label={t("chatsession.select_mode", { defaultValue: "Select" })}
                        />
                      </DropdownMenuItem>
                    )}
                  </>
                )}

                {canLeaveOrHideSharedSession && (
                  <DropdownMenuItem
                    className="gap-2"
                    disabled={leaveSharedSession.isPending}
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowLeaveSharedDialog(true)
                    }}
                  >
                    <SidebarChatMenuItemContent
                      label={sharedActionLabel}
                      icon={
                        hasDirectShare ? (
                          <UserMinus className="size-4" />
                        ) : (
                          <EyeOff className="size-4" />
                        )
                      }
                    />
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {!isRenaming && selectMode && (
            <div className="absolute top-1/2 right-1 -translate-y-1/2 rounded-md p-1">
              {selectedSessions.some((s) => s != null && s.id === session.id) ? (
                <CheckCircle2 className="text-primary size-5" />
              ) : (
                <Circle className="text-muted-foreground size-5" />
              )}
            </div>
          )}
        </div>
        <TooltipContent
          side="right"
          sideOffset={8}
          className={cn(
            session.pending_title_suggestion ? "max-w-[380px] p-2.5" : "max-w-[280px] p-3"
          )}
        >
          {session.pending_title_suggestion ? (
            <div className="flex items-start gap-2.5">
              <div className="flex min-w-0 flex-1 items-start gap-2">
                <span className="text-primary mt-0.5 shrink-0">
                  <Lightbulb className="size-4" />
                </span>
                <div className="min-w-0">
                  <p className="max-w-[19rem] text-sm leading-5 break-words whitespace-normal">
                    "{session.pending_title_suggestion.suggested_title}"
                  </p>
                  <p className="text-muted-foreground mt-1 text-xs">
                    {t("dynamic_titles.new_title_suggestion", {
                      defaultValue: "New title suggestion",
                    })}
                  </p>
                </div>
              </div>
              <div className="mt-0.5 flex shrink-0 items-center gap-1">
                <Button
                  aria-label={t("dynamic_titles.action_dismiss", { defaultValue: "Dismiss" })}
                  className="text-muted-foreground/85 hover:text-foreground h-6 w-6 transition-colors"
                  disabled={isUpdatingTitleSuggestion}
                  size="icon"
                  variant="ghost"
                  onClick={(event) => void handleDismissTitleSuggestion(event)}
                >
                  <X className="h-3 w-3" />
                </Button>
                <Button
                  aria-label={t("dynamic_titles.action_accept", { defaultValue: "Accept" })}
                  className="border-primary-ink/40 text-primary-ink/90 hover:bg-primary/10 hover:text-primary-ink h-6 w-6 transition-colors"
                  disabled={isUpdatingTitleSuggestion}
                  size="icon"
                  variant="outline"
                  onClick={(event) => void handleAcceptTitleSuggestion(event)}
                >
                  <Check className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {getIconGlyph()}
                <p className="text-sm font-medium">{session.title}</p>
                {!hideSharing && session.participants && session.participants.length > 0 && (
                  <Users className="text-muted-foreground size-3.5 shrink-0" />
                )}
              </div>
              {session.hasUnviewedNotification && session.invitationNotification ? (
                <p className="text-muted-foreground text-xs">
                  {t("chatsession.invitation_notification_tooltip", {
                    name: session.invitationNotification.invitedByDisplayName || "Someone",
                    accessMode: (() => {
                      const mode = session.invitationNotification.messageAccessMode
                      if (mode === "full_history") {
                        return t("sharing.access_full_history_badge")
                      }
                      if (mode === "partial_history") {
                        return t("sharing.access_partial_history_badge")
                      }
                      return t("sharing.access_specific_message_badge")
                    })(),
                    defaultValue: "{{name}} shared this chat with you ({{accessMode}})",
                  })}
                </p>
              ) : (
                <p className="text-muted-foreground text-xs">
                  {t("chatsession.last_modified")}
                  {session.updated &&
                    format(new Date(session.updated), "yyyy-MM-dd HH:mm", {
                      locale: sv,
                    })}
                </p>
              )}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
      <MoveToProjectDialog
        open={showMoveDialog}
        sessionId={session.id}
        sessionType={session.sessionType ?? "open"}
        onOpenChange={setShowMoveDialog}
        onProjectSelected={handleMoveToProject}
      />
      {canLeaveOrHideSharedSession && (
        <AlertDialog open={showLeaveSharedDialog} onOpenChange={setShowLeaveSharedDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{getSharedSessionConfirmTitle(t, hasDirectShare)}</AlertDialogTitle>
              <AlertDialogDescription>
                {getSharedSessionConfirmDescription(t, hasDirectShare, hasGroupShare)}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {t("common.cancel", {
                  defaultValue: "Cancel",
                })}
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={leaveSharedSession.isPending}
                onClick={(e) => {
                  e.preventDefault()
                  void handleConfirmLeaveOrHideSharedSession()
                }}
              >
                {leaveSharedSession.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
                {sharedActionLabel}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {!hideSharing && session.id && (
        <ChatShareDialog
          chatStore={chatStore}
          open={showShareDialog}
          session={session}
          sessionId={session.id}
          onLeave={onSharedLeaveRedirect}
          onOpenChange={setShowShareDialog}
        />
      )}
    </>
  )
}

export const ChatHistoryItem = memo(ChatHistoryItemInner)
